import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Trash2, Pencil, Send, X, Check, AtSign } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/dates";

export default function Comments({ entityType, entityId, compact = false }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/comments", { params: { entity_type: entityType, entity_id: entityId } });
      setItems(r.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const r = await api.get("/users");
      setUsers((r.data || []).filter((u) => u.active !== false));
    } catch { setUsers([]); }
  };

  useEffect(() => {
    if (entityId) { load(); loadUsers(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const extractMentionIds = (text) => {
    // Match @"Full Name" tokens and resolve to user_ids
    const re = /@"([^"]+)"/g;
    const ids = new Set();
    let m;
    while ((m = re.exec(text)) !== null) {
      const name = m[1].trim().toLowerCase();
      const u = users.find((x) => (x.name || "").toLowerCase() === name);
      if (u) ids.add(u.user_id);
    }
    return Array.from(ids);
  };

  const post = async () => {
    if (!body.trim()) return;
    setPosting(true);
    try {
      await api.post("/comments", {
        entity_type: entityType,
        entity_id: entityId,
        body: body.trim(),
        mention_user_ids: extractMentionIds(body),
      });
      setBody("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to post");
    }
    setPosting(false);
  };

  const startEdit = (c) => { setEditingId(c.id); setEditBody(c.body); };
  const cancelEdit = () => { setEditingId(null); setEditBody(""); };

  const saveEdit = async () => {
    if (!editBody.trim()) return;
    try {
      await api.patch(`/comments/${editingId}`, {
        body: editBody.trim(),
        mention_user_ids: extractMentionIds(editBody),
      });
      cancelEdit();
      load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${id}`);
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const canModify = (c) => c.author_id === user.user_id || user.role === "Admin";
  const fmt = (iso) => fmtDateTime(iso);

  return (
    <div data-testid="comments-section">
      {!compact && (
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare size={14} className="text-slate-500" />
          <div className="gc-label">Discussion ({items.length})</div>
        </div>
      )}

      <div className="space-y-2.5">
        {loading && <div className="text-xs text-slate-400">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-xs text-slate-400 py-3">No comments yet — start the conversation.</div>
        )}
        {items.map((c) => (
          <div key={c.id} className="flex gap-2.5" data-testid={`comment-${c.id}`}>
            <Avatar name={c.author_name} picture={c.author_picture} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-[#0a2540]">{c.author_name}</span>
                <span className="text-slate-400 font-mono-data text-[10px]">{fmt(c.created_at)}</span>
                {c.updated_at && <span className="text-slate-400 text-[10px]">(edited)</span>}
                {canModify(c) && editingId !== c.id && (
                  <div className="ml-auto inline-flex items-center gap-0.5">
                    <button onClick={() => startEdit(c)} className="text-slate-400 hover:text-[#0a2540] p-1" data-testid={`btn-edit-${c.id}`}>
                      <Pencil size={11} />
                    </button>
                    <button onClick={() => remove(c.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`btn-delete-${c.id}`}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                )}
              </div>
              {editingId === c.id ? (
                <div className="mt-1">
                  <MentionTextarea
                    value={editBody}
                    onChange={setEditBody}
                    users={users}
                    rows={3}
                    testId={`input-edit-${c.id}`}
                  />
                  <div className="mt-1.5 flex items-center gap-2 justify-end">
                    <Button size="sm" variant="ghost" className="h-7" onClick={cancelEdit}><X size={12} className="mr-1" /> Cancel</Button>
                    <Button size="sm" className="h-7 bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={saveEdit} data-testid={`btn-save-edit-${c.id}`}><Check size={12} className="mr-1" /> Save</Button>
                  </div>
                </div>
              ) : (
                <RenderedBody body={c.body} users={users} mentionIds={c.mention_user_ids || []} />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Composer */}
      <div className="mt-4 flex gap-2.5">
        <Avatar name={user.name} picture={user.picture} />
        <div className="flex-1">
          <MentionTextarea
            value={body}
            onChange={setBody}
            users={users}
            rows={compact ? 2 : 3}
            placeholder="Post an update… Type @ to mention someone."
            testId="input-new-comment"
            onSubmit={post}
          />
          <div className="mt-1.5 flex items-center justify-between">
            <div className="text-[10px] text-slate-400 inline-flex items-center gap-1"><AtSign size={10} /> Type @ to mention · ⌘/Ctrl + Enter to send</div>
            <Button size="sm" disabled={posting || !body.trim()} onClick={post} className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm h-8" data-testid="btn-post-comment">
              <Send size={12} className="mr-1.5" /> {posting ? "Posting…" : "Post"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RenderedBody({ body, users }) {
  // Render @"Name" tokens as styled mention chips.
  const parts = useMemo(() => {
    const out = [];
    const re = /@"([^"]+)"/g;
    let last = 0;
    let m;
    while ((m = re.exec(body)) !== null) {
      if (m.index > last) out.push({ t: "text", v: body.slice(last, m.index) });
      const name = m[1];
      const u = users.find((x) => (x.name || "").toLowerCase() === name.toLowerCase());
      out.push({ t: "mention", v: name, known: !!u });
      last = m.index + m[0].length;
    }
    if (last < body.length) out.push({ t: "text", v: body.slice(last) });
    return out;
  }, [body, users]);
  return (
    <div className="mt-0.5 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
      {parts.map((p, i) =>
        p.t === "mention" ? (
          <span key={i} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-xs font-medium ${p.known ? "bg-[#0a2540]/10 text-[#0a2540]" : "bg-slate-100 text-slate-500"}`}>
            @{p.v}
          </span>
        ) : (
          <span key={i}>{p.v}</span>
        )
      )}
    </div>
  );
}

function MentionTextarea({ value, onChange, users, rows = 3, placeholder, testId, onSubmit }) {
  const ref = useRef(null);
  const [picker, setPicker] = useState(null); // { start, query, selectedIdx }

  const filtered = useMemo(() => {
    if (!picker) return [];
    const q = picker.query.toLowerCase();
    return users
      .filter((u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
      .slice(0, 6);
  }, [picker, users]);

  const handleChange = (e) => {
    const v = e.target.value;
    const caret = e.target.selectionStart;
    onChange(v);
    // Detect @query: look backwards from caret to find the last '@' that isn't escaped
    const upto = v.slice(0, caret);
    const at = upto.lastIndexOf("@");
    if (at >= 0) {
      const after = upto.slice(at + 1);
      // Only trigger if it's a fresh @ word (no quote, no newline, no space immediately after @)
      if (!/[\s"]/.test(after) && after.length <= 30) {
        setPicker({ start: at, query: after, selectedIdx: 0 });
        return;
      }
    }
    setPicker(null);
  };

  const insertMention = (u) => {
    if (!picker || !ref.current) return;
    const ta = ref.current;
    const v = value;
    const before = v.slice(0, picker.start);
    const after = v.slice(ta.selectionStart);
    const token = `@"${u.name}" `;
    const next = before + token + after;
    onChange(next);
    setPicker(null);
    requestAnimationFrame(() => {
      const pos = (before + token).length;
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  };

  const handleKeyDown = (e) => {
    if (picker && filtered.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setPicker({ ...picker, selectedIdx: (picker.selectedIdx + 1) % filtered.length });
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setPicker({ ...picker, selectedIdx: (picker.selectedIdx - 1 + filtered.length) % filtered.length });
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(filtered[picker.selectedIdx]);
        return;
      }
      if (e.key === "Escape") {
        setPicker(null);
        return;
      }
    }
    if (onSubmit && e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setPicker(null), 150)}
        placeholder={placeholder}
        rows={rows}
        className="rounded-sm text-sm"
        data-testid={testId}
      />
      {picker && filtered.length > 0 && (
        <div className="absolute z-30 mt-1 left-3 bg-white border border-slate-200 shadow-md rounded-sm min-w-[220px] max-w-[300px]" data-testid="mention-picker">
          {filtered.map((u, i) => (
            <button
              key={u.user_id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 inline-flex items-center gap-2 ${i === picker.selectedIdx ? "bg-slate-50" : ""}`}
              data-testid={`mention-option-${u.user_id}`}
            >
              <Avatar name={u.name} picture={u.picture} size={20} />
              <span className="flex-1 min-w-0">
                <div className="font-medium text-[#0a2540] truncate">{u.name}</div>
                <div className="font-mono-data text-[10px] text-slate-500 truncate">{u.email}</div>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Avatar({ name, picture, size = 32 }) {
  const dim = { width: size, height: size };
  if (picture) {
    return <img src={picture} alt={name} style={dim} className="rounded-full shrink-0" />;
  }
  return (
    <div className="rounded-full bg-[#0a2540] text-white text-xs font-semibold inline-flex items-center justify-center shrink-0" style={{ ...dim, fontFamily: "Outfit" }}>
      {(name || "?").trim()[0]?.toUpperCase()}
    </div>
  );
}
